import { gql } from '@apollo/client';

export const GET_RISK_SCORECARD = gql`
  query GetRiskScorecard($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        riskScorecardHistory {
          id
          name
          date
          totalScore
          dealStructureSectionScore
          businessModelSectionScore
          operatingCashFlowSectionScore
          marketPositionSectionScore
          industryResilienceSectionScore
          sponsorManagementSectionScore
        }
        lastRiskScorecardHistory {
          id
          name
          date
          totalScore
        }
        attachDetachFromCap
        leverageAttachmentPoint
        leverageDetachmentPoint
        leverageAttachmentPointCalc
        leverageDetachmentPointCalc
        leverageAttachmentPointScore
        leverageDetachmentPointScore
        dealStructureSectionScore
        equityAsPercentOfTotalCapital
        equityAsPercentOfTotalCapitalScore
        useOfProceeds
        useOfProceedsScore
        dealStructureSynthesis
        businessModelSectionScore
        revenueModel
        revenueModelScore
        top10CustomerConcentrationScore
        organicRevenueCagr
        organicRevenueCagrScore
        organicRevenueGrowth
        organicRevenueGrowthScore
        businessType
        serviceGrossMarginThresholds
        productGrossMarginThresholds
        serviceGrossMarginThresholdsScore
        productGrossMarginThresholdsScore
        businessModelSynthesis
        operatingCashFlowSectionScore
        ebitdaMargins
        ebitdaMarginsScore
        ebitdaCapexInterest1
        ebitdaCapexInterest1Score
        interestCoverageRatio
        interestCoverageRatioScore
        fcfConversionPercentage
        fcfConversionPercentageScore
        operatingCashFlowSynthesis
        marketPositionSectionScore
        marketPositionScore
        marketAttributesCount
        marketAttributesSynthesis
        industryResilienceSectionScore
        industryResilience
        industryResilienceScore
        industryResilienceSynthesis
        sponsorManagementSectionScore
        sponsorManagementScore
        sponsorManagementSynthesis
        totalScore
      }
    }
  }
`;

export const GET_RISK_SCORECARD_HISTORY_DETAIL = gql`
  query GetRiskScorecardHistoryDetail($id: ID!) {
    node(id: $id) {
      ... on OpportunityRiskScorecardHistoryNode {
        id
        name
        date
        totalScore
        dealStructureSectionScore
        leverageAttachmentPointScore
        leverageDetachmentPointScore
        equityAsPercentOfTotalCapitalScore
        useOfProceedsScore
        dealStructureSynthesis
        businessModelSectionScore
        revenueModelScore
        top10CustomerConcentrationScore
        organicRevenueCagrScore
        organicRevenueGrowthScore
        serviceGrossMarginThresholdsScore
        productGrossMarginThresholdsScore
        businessModelSynthesis
        operatingCashFlowSectionScore
        ebitdaMarginsScore
        ebitdaCapexInterest1Score
        interestCoverageRatioScore
        fcfConversionPercentageScore
        operatingCashFlowSynthesis
        marketPositionSectionScore
        marketPositionScore
        industryResilienceSectionScore
        industryResilienceScore
        industryResilienceSynthesis
        sponsorManagementSectionScore
        sponsorManagementScore
        sponsorManagementSynthesis
      }
    }
  }
`;

export const UPDATE_RISK_SCORECARD = gql`
  mutation UpdateOpportunityRiskScorecard(
    $id: ID!
    $values: RiskScorecardInput!
  ) {
    updateOpportunityRiskScorecard(id: $id, values: $values) {
      ok
      opportunity {
        id
        totalScore
      }
    }
  }
`;

export const CREATE_RISK_SCORECARD_HISTORY = gql`
  mutation CreateRiskScorecardHistory(
    $values: CreateRiskScorecardHistoryInput!
  ) {
    createRiskScorecardHistory(values: $values) {
      ok
      history {
        id
        name
        date
        totalScore
      }
    }
  }
`;

export interface CreateRiskScorecardHistoryResponse {
  createRiskScorecardHistory: {
    history: null | RiskScorecardHistoryItem;
    ok: boolean;
  };
}

export interface GetRiskScorecardHistoryDetailResponse {
  node: null | RiskScorecardHistoryDetail;
}

export interface GetRiskScorecardResponse {
  node:
    | ({
        __typename: 'OpportunityNode';
      } & RiskScorecardData)
    | null;
}

export interface RiskScorecardData {
  attachDetachFromCap: boolean | null;
  businessModelSectionScore: null | number;
  businessModelSynthesis: null | string;
  businessType: null | string;
  dealStructureSectionScore: null | number;
  dealStructureSynthesis: null | string;
  ebitdaCapexInterest1: null | number;
  ebitdaCapexInterest1Score: null | number;
  ebitdaMargins: null | number;
  ebitdaMarginsScore: null | number;
  equityAsPercentOfTotalCapital: null | number;
  equityAsPercentOfTotalCapitalScore: null | number;
  fcfConversionPercentage: null | number;
  fcfConversionPercentageScore: null | number;
  id: string;
  industryResilience: null | string;
  industryResilienceScore: null | number;
  industryResilienceSectionScore: null | number;
  industryResilienceSynthesis: null | string;
  interestCoverageRatio: null | number;
  interestCoverageRatioScore: null | number;
  lastRiskScorecardHistory: null | RiskScorecardHistoryItem;
  leverageAttachmentPoint: null | string;
  leverageAttachmentPointCalc: null | string;
  leverageAttachmentPointScore: null | number;
  leverageDetachmentPoint: null | string;
  leverageDetachmentPointCalc: null | string;
  leverageDetachmentPointScore: null | number;
  marketAttributesCount: null | number;
  marketAttributesSynthesis: null | string;
  marketPositionScore: null | number;
  marketPositionSectionScore: null | number;
  operatingCashFlowSectionScore: null | number;
  operatingCashFlowSynthesis: null | string;
  organicRevenueCagr: null | number;
  organicRevenueCagrScore: null | number;
  organicRevenueGrowth: null | number;
  organicRevenueGrowthScore: null | number;
  productGrossMarginThresholds: null | number;
  productGrossMarginThresholdsScore: null | number;
  revenueModel: null | string;
  revenueModelScore: null | number;
  riskScorecardHistory: RiskScorecardHistoryItem[];
  serviceGrossMarginThresholds: null | number;
  serviceGrossMarginThresholdsScore: null | number;
  sponsorManagementScore: null | number;
  sponsorManagementSectionScore: null | number;
  sponsorManagementSynthesis: null | string;
  top10CustomerConcentrationScore: null | number;
  totalScore: null | number;
  useOfProceeds: null | string;
  useOfProceedsScore: null | number;
}

export interface RiskScorecardHistoryDetail {
  businessModelSectionScore: null | number;
  businessModelSynthesis: null | string;
  date: null | string;
  dealStructureSectionScore: null | number;
  dealStructureSynthesis: null | string;
  ebitdaCapexInterest1Score: null | number;
  ebitdaMarginsScore: null | number;
  equityAsPercentOfTotalCapitalScore: null | number;
  fcfConversionPercentageScore: null | number;
  id: string;
  industryResilienceScore: null | number;
  industryResilienceSectionScore: null | number;
  industryResilienceSynthesis: null | string;
  interestCoverageRatioScore: null | number;
  leverageAttachmentPointScore: null | number;
  leverageDetachmentPointScore: null | number;
  marketPositionScore: null | number;
  marketPositionSectionScore: null | number;
  name: null | string;
  operatingCashFlowSectionScore: null | number;
  operatingCashFlowSynthesis: null | string;
  organicRevenueCagrScore: null | number;
  organicRevenueGrowthScore: null | number;
  productGrossMarginThresholdsScore: null | number;
  revenueModelScore: null | number;
  serviceGrossMarginThresholdsScore: null | number;
  sponsorManagementScore: null | number;
  sponsorManagementSectionScore: null | number;
  sponsorManagementSynthesis: null | string;
  top10CustomerConcentrationScore: null | number;
  totalScore: null | number;
  useOfProceedsScore: null | number;
}

export interface RiskScorecardHistoryItem {
  businessModelSectionScore: null | number;
  date: null | string;
  dealStructureSectionScore: null | number;
  id: string;
  industryResilienceSectionScore: null | number;
  marketPositionSectionScore: null | number;
  name: null | string;
  operatingCashFlowSectionScore: null | number;
  sponsorManagementSectionScore: null | number;
  totalScore: null | number;
}

export interface RiskScorecardInput {
  attachDetachFromCap?: boolean;
  businessModelSynthesis?: string;
  businessType?: string;
  dealStructureSynthesis?: string;
  ebitdaCapexInterest1?: number;
  ebitdaMargins?: number;
  equityAsPercentOfTotalCapital?: number;
  fcfConversionPercentage?: number;
  industryResilience?: string;
  industryResilienceSynthesis?: string;
  interestCoverageRatio?: number;
  leverageAttachmentPoint?: string;
  leverageDetachmentPoint?: string;
  marketAttributesCount?: number;
  marketAttributesSynthesis?: string;
  operatingCashFlowSynthesis?: string;
  organicRevenueCagr?: number;
  organicRevenueGrowth?: number;
  productGrossMarginThresholds?: number;
  revenueModel?: string;
  serviceGrossMarginThresholds?: number;
  sponsorManagementScore?: number;
  sponsorManagementSynthesis?: string;
  useOfProceeds?: string;
}

export interface UpdateRiskScorecardResponse {
  updateOpportunityRiskScorecard: {
    ok: boolean;
    opportunity: { id: string; totalScore: null | number } | null;
  };
}

export const formatDate = (dateString: null | string | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const formatScore = (score: null | number | undefined): string => {
  if (score == null) return '-';
  return `${Math.round(score)}%`;
};

export const formatScoreOutOf5 = (score: null | number | undefined): string => {
  if (score == null) return '-';
  return `${Math.round(score)}/5`;
};

export const getScoreColor = (score: null | number | undefined): string => {
  if (score == null) return '';
  if (score < 33) return 'text-destructive';
  if (score < 66) return 'text-yellow-500';
  return 'text-green-500';
};
